/**
 * @xivdyetools/core - API Service
 *
 * Universalis API integration with caching and debouncing
 * Environment-agnostic with pluggable cache backends
 *
 * @module services/APIService
 */
import { ErrorCode, AppError } from '@xivdyetools/types';
import { NoOpLogger } from '@xivdyetools/logger/library';
import { UNIVERSALIS_API_BASE, UNIVERSALIS_API_TIMEOUT, UNIVERSALIS_API_RETRY_COUNT, UNIVERSALIS_API_RETRY_DELAY, API_CACHE_TTL, API_RATE_LIMIT_DELAY, API_CACHE_VERSION, API_MAX_RESPONSE_SIZE, } from '../constants/index.js';
import { retry, sleep, generateChecksum } from '../utils/index.js';
/**
 * Default fetch client using global fetch API
 */
export class DefaultFetchClient {
    async fetch(url, options) {
        return fetch(url, options);
    }
}
/**
 * HTTP error carrying the response status.
 * OPT-014 (2026-07-18 audit): lets retry policies distinguish deterministic
 * 4xx failures (fail fast) from transient 429/5xx/network errors (retry).
 */
export class HttpError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.status = status;
        this.name = 'HttpError';
    }
}
/**
 * OPT-014: retry predicate — 4xx (except 429) is deterministic, don't retry.
 * Non-HttpError failures (network, timeout, parse) stay retryable.
 */
export function isRetryableHttpError(error) {
    if (error instanceof HttpError) {
        return error.status === 429 || error.status >= 500;
    }
    return true;
}
/**
 * Default rate limiter with configurable minimum delay between requests
 *
 * BUG-046 (2026-07-18 audit): reserves the next slot SYNCHRONOUSLY before any
 * await, so N concurrent callers space out by minDelay instead of all reading
 * the same lastRequestTime and bursting through together.
 */
export class DefaultRateLimiter {
    minDelay;
    nextAvailable = 0;
    /**
     * Constructor with optional minimum delay
     * @param minDelay Minimum milliseconds between requests (default: API_RATE_LIMIT_DELAY)
     */
    constructor(minDelay = API_RATE_LIMIT_DELAY) {
        this.minDelay = minDelay;
    }
    async waitIfNeeded() {
        const now = Date.now();
        const slot = Math.max(now, this.nextAvailable);
        // Reserve before awaiting — synchronous update means no interleaving
        this.nextAvailable = slot + this.minDelay;
        if (slot > now) {
            await sleep(slot - now);
        }
    }
    recordRequest() {
        // No-op: slot reservation is folded into waitIfNeeded (BUG-046).
        // Kept for RateLimiter interface compatibility.
    }
}
/**
 * In-memory cache backend (default, no persistence)
 */
export class MemoryCacheBackend {
    cache = new Map();
    get(key) {
        return this.cache.get(key) || null;
    }
    set(key, value) {
        this.cache.set(key, value);
    }
    delete(key) {
        this.cache.delete(key);
    }
    clear() {
        this.cache.clear();
    }
    keys() {
        return Array.from(this.cache.keys());
    }
}
/**
 * Extract price and worldId from a Universalis API item result
 * CORE-REF-002 FIX: Centralized price extraction logic used by both single and batch parsing.
 *
 * Priority order (NQ only - dyes in FFXIV are always Normal Quality):
 * 1. Data Center price (preferred for cross-world comparison)
 * 2. World price (fallback)
 * 3. Region price (last resort)
 *
 * Note: HQ prices are intentionally not checked because dyes do not have HQ variants in FFXIV.
 *
 * @param result - The item result from Universalis API
 * @returns Object containing price (or null) and optional worldId
 */
function extractPriceFromApiItem(result) {
    let price = null;
    let worldId = undefined;
    // Extract NQ prices only (dyes in FFXIV are always NQ - no HQ variants exist)
    // Priority: DC → World → Region
    if (result.nq?.minListing) {
        const listing = result.nq.minListing;
        if (listing.dc?.price) {
            price = listing.dc.price;
            worldId = listing.dc.worldId;
        }
        else if (listing.world?.price) {
            price = listing.world.price;
            worldId = listing.world.worldId;
        }
        else if (listing.region?.price) {
            price = listing.region.price;
            worldId = listing.region.worldId;
        }
    }
    return { price, worldId };
}
/**
 * Service for Universalis API integration
 * Handles price data fetching with caching and debouncing
 *
 * Refactored for testability: Supports dependency injection of cache, fetch client, and rate limiter
 *
 * @example
 * // With default implementations (memory cache, global fetch, standard rate limiting)
 * const apiService = new APIService();
 *
 * // With custom cache backend (e.g., Redis)
 * const redisCache = new RedisCacheBackend(redisClient);
 * const apiService = new APIService({ cacheBackend: redisCache });
 *
 * // With custom fetch client for testing
 * const mockFetch = new MockFetchClient();
 * const apiService = new APIService({ fetchClient: mockFetch });
 *
 * // With custom rate limiter (e.g., no rate limiting for tests)
 * const noRateLimit = new NoOpRateLimiter();
 * const apiService = new APIService({ rateLimiter: noRateLimit });
 *
 * // Fetch price data
 * const priceData = await apiService.getPriceData(itemID, worldID, dataCenterID);
 *
 * // With custom logger for debugging
 * import { ConsoleLogger } from 'xivdyetools-core';
 * const apiService = new APIService({ logger: ConsoleLogger });
 *
 * // With custom base URL (e.g., CORS proxy for browser clients)
 * const apiService = new APIService({
 *   baseUrl: 'https://data.xivdyetools.app/universalis'
 * });
 */
export class APIService {
    cache;
    fetchClient;
    rateLimiter;
    pendingRequests = new Map();
    /**
     * OPT-001 (2026-07-18 audit): in-flight batch coalescing — identical
     * concurrent batch fetches share one upstream request (cold-cache stampede
     * protection, mirroring pendingRequests for the single-item path). The
     * shared result carries the failure flag too, so every coalesced caller
     * reports the same outcome (BUG-090).
     */
    pendingBatchRequests = new Map();
    logger;
    baseUrl;
    /**
     * Constructor with optional dependency injection
     * @param options Configuration options
     */
    constructor(options = {}) {
        this.cache = options.cacheBackend ?? new MemoryCacheBackend();
        this.fetchClient = options.fetchClient ?? new DefaultFetchClient();
        this.rateLimiter = options.rateLimiter ?? new DefaultRateLimiter();
        this.logger = options.logger ?? NoOpLogger;
        this.baseUrl = options.baseUrl ?? UNIVERSALIS_API_BASE;
    }
    // ============================================================================
    // Cache Management
    // ============================================================================
    /**
     * Get price from cache if available and not expired
     * Validates cache version and checksum
     *
     * ERROR-001: On cache backend errors, logs and returns null to fall through to API fetch.
     * This prevents cache failures from blocking price lookups.
     */
    async getCachedPrice(cacheKey) {
        let cached;
        try {
            cached = await this.cache.get(cacheKey);
        }
        catch (error) {
            // ERROR-001: Log cache backend error and fall through to API fetch
            // This distinguishes "cache broken" from "cache miss" in logs
            this.logger.error(`Cache backend error for key ${cacheKey}: ${error instanceof Error ? error.message : 'Unknown error'}. Falling through to API fetch.`);
            return null;
        }
        if (!cached) {
            return null;
        }
        // Check cache version
        if (cached.version && cached.version !== API_CACHE_VERSION) {
            // OPT-003: Fire-and-forget — cache cleanup is best-effort, don't block request path
            void this.cache.delete(cacheKey);
            return null;
        }
        // Check if cache has expired
        if (Date.now() - cached.timestamp > cached.ttl) {
            // OPT-003: Fire-and-forget — cache cleanup is best-effort, don't block request path
            void this.cache.delete(cacheKey);
            return null;
        }
        // Validate checksum if present
        if (cached.checksum) {
            const computedChecksum = generateChecksum(cached.data);
            if (computedChecksum !== cached.checksum) {
                this.logger.warn(`Cache corruption detected for key: ${cacheKey}`);
                // OPT-003: Fire-and-forget — cache cleanup is best-effort, don't block request path
                void this.cache.delete(cacheKey);
                return null;
            }
        }
        return cached.data;
    }
    /**
     * Set price in cache with version and checksum
     */
    async setCachedPrice(cacheKey, data) {
        const checksum = generateChecksum(data);
        await this.cache.set(cacheKey, {
            data,
            timestamp: Date.now(),
            ttl: API_CACHE_TTL,
            version: API_CACHE_VERSION,
            checksum,
        });
    }
    /**
     * Best-effort cache write.
     * BUG-011 (2026-07-18 audit): symmetric with ERROR-001's failure-tolerant
     * reads — a failed cache write (quota exceeded, KV error) must never
     * discard successfully fetched price data.
     */
    async trySetCachedPrice(cacheKey, data) {
        try {
            await this.setCachedPrice(cacheKey, data);
        }
        catch (error) {
            this.logger.error(`Cache write failed for ${cacheKey}: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    /**
     * Clear cache
     */
    async clearCache() {
        await this.cache.clear();
        this.logger.info('Price cache cleared');
    }
    // ============================================================================
    // API Calls
    // ============================================================================
    /**
     * Fetch price data for a dye from Universalis API
     * Implements caching, debouncing, and retry logic
     *
     * CORE-BUG-001/002 FIX: Uses atomic deduplication pattern to prevent race conditions.
     * The promise is stored in the map BEFORE the async operation begins, ensuring that
     * concurrent calls will always see the pending request even if they arrive during
     * the cache check phase.
     */
    async getPriceData(itemID, worldID, dataCenterID) {
        // Build cache key first (synchronous)
        const cacheKey = this.buildCacheKey(itemID, worldID, dataCenterID);
        // CORE-BUG-001 FIX: Check pending requests BEFORE any async operations
        // This prevents the race condition where two calls both pass the pending check
        // before either sets their promise in the map
        const existingRequest = this.pendingRequests.get(cacheKey);
        if (existingRequest) {
            this.logger.info(`Using pending request for item ${itemID}`);
            return existingRequest;
        }
        // CORE-BUG-001 FIX: Create and store the promise SYNCHRONOUSLY before starting
        // any async work. We use a deferred promise pattern to ensure the map entry
        // exists before we await anything.
        let resolvePromise;
        const promise = new Promise((resolve) => {
            resolvePromise = resolve;
            // Note: We don't use reject because getPriceData returns null on error
            // to maintain existing API behavior (non-throwing)
        });
        // Store immediately - this is synchronous and happens before any await
        this.pendingRequests.set(cacheKey, promise);
        try {
            // Now we can safely do async operations
            const cached = await this.getCachedPrice(cacheKey);
            if (cached) {
                this.logger.info(`Price cache hit for item ${itemID}`);
                this.pendingRequests.delete(cacheKey);
                resolvePromise(cached);
                return cached;
            }
            // Fetch from API
            const data = await this.fetchPriceData(itemID, worldID, dataCenterID);
            // CORE-BUG-002 FIX: Always delete from pending map before resolving/rejecting
            // This ensures cleanup happens in a predictable order
            this.pendingRequests.delete(cacheKey);
            if (data) {
                // BUG-011 (2026-07-18 audit): a cache-write failure must not discard
                // successfully fetched data
                await this.trySetCachedPrice(cacheKey, data);
            }
            resolvePromise(data);
            return data;
        }
        catch (error) {
            // CORE-BUG-002 FIX: Clean up on error path as well
            this.pendingRequests.delete(cacheKey);
            this.logger.error('Failed to fetch price data', error);
            // Resolve with null instead of rejecting to maintain existing behavior
            // where errors return null rather than throwing
            resolvePromise(null);
            return null;
        }
    }
    /**
     * Internal method to fetch price data from API
     */
    async fetchPriceData(itemID, worldID, dataCenterID) {
        // Rate limiting: use injected rate limiter
        await this.rateLimiter.waitIfNeeded();
        this.rateLimiter.recordRequest();
        // Build API URL
        const url = this.buildApiUrl(itemID, worldID, dataCenterID);
        try {
            const data = await retry(() => this.fetchWithTimeout(url, UNIVERSALIS_API_TIMEOUT), UNIVERSALIS_API_RETRY_COUNT, UNIVERSALIS_API_RETRY_DELAY, this.logger, isRetryableHttpError);
            if (!data) {
                return null;
            }
            // Parse and validate response
            return this.parseApiResponse(data, itemID);
        }
        catch (error) {
            // SECURITY: Log detailed error internally but provide sanitized message to callers
            // This prevents exposing internal API structure or upstream error details
            this.logger.error(`Failed to fetch price data for item ${itemID}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            throw new AppError(ErrorCode.API_CALL_FAILED, `Failed to fetch price data for item ${itemID}`, 'warning');
        }
    }
    /**
     * Fetch batch price data from API for multiple items in a single request
     * This is more efficient than making individual requests for each item
     *
     * BUG-090 (2026-10-04 deep-dive): failures are still absorbed (callers
     * get whatever prices did arrive), but no longer silently — `failed` says
     * whether any upstream request behind the Map failed, so an outage is not
     * mistaken for "no listings".
     *
     * @param itemIDs - Array of item IDs to fetch prices for
     * @param dataCenterID - Optional data center ID (e.g., "Crystal")
     * @returns The prices of every successfully fetched item, and whether any
     *   request failed
     */
    async fetchBatchPriceData(itemIDs, dataCenterID) {
        if (itemIDs.length === 0) {
            return { prices: new Map(), failed: false };
        }
        // BUG-001: Universalis caps batches at 100 items. buildBatchApiUrl throws for larger
        // arrays, and the call below was outside the try/catch — uncaught on cold cache with
        // the full dye set (125+ tradeable). Chunk here so callers never need to know the limit.
        const CHUNK_SIZE = 100;
        if (itemIDs.length > CHUNK_SIZE) {
            const merged = new Map();
            let failed = false;
            for (let offset = 0; offset < itemIDs.length; offset += CHUNK_SIZE) {
                const chunk = itemIDs.slice(offset, offset + CHUNK_SIZE);
                const chunkResult = await this.fetchBatchPriceData(chunk, dataCenterID);
                for (const [id, data] of chunkResult.prices) {
                    merged.set(id, data);
                }
                failed = failed || chunkResult.failed;
            }
            return { prices: merged, failed };
        }
        // BUG-012 (2026-07-18 audit): buildBatchApiUrl throws for non-positive-
        // integer IDs (e.g. Facewear synthetic negatives leaking past a caller's
        // filter) and sat outside the try below — turning "no price for that
        // item" into an uncaught AppError. Filter and warn instead.
        const validItemIDs = itemIDs.filter((id) => Number.isInteger(id) && id > 0);
        if (validItemIDs.length < itemIDs.length) {
            this.logger.warn(`Skipping ${itemIDs.length - validItemIDs.length} invalid item IDs in batch fetch`);
        }
        if (validItemIDs.length === 0) {
            return { prices: new Map(), failed: false };
        }
        // Build batch API URL — safe: length ≤ 100 and IDs validated above
        const url = this.buildBatchApiUrl(validItemIDs, dataCenterID);
        try {
            // Rate limiting: single request for the batch. Inside the try (Sprint 27
            // review): an injected limiter that fails is a failed request, not a
            // rejection out of a method documented as never throwing.
            await this.rateLimiter.waitIfNeeded();
            this.rateLimiter.recordRequest();
            const data = await retry(() => this.fetchWithTimeout(url, UNIVERSALIS_API_TIMEOUT), UNIVERSALIS_API_RETRY_COUNT, UNIVERSALIS_API_RETRY_DELAY, this.logger, isRetryableHttpError);
            // Parse batch response. A body that is not a batch response at all (a
            // JSON `null`, no results array) is a failed request, not "no listings".
            const prices = this.parseBatchApiResponse(data);
            return prices ? { prices, failed: false } : { prices: new Map(), failed: true };
        }
        catch (error) {
            this.logger.error(`Failed to fetch batch price data for ${itemIDs.length} items: ${error instanceof Error ? error.message : 'Unknown error'}`);
            return { prices: new Map(), failed: true };
        }
    }
    /**
     * Fetch with timeout and size limits
     * Uses injected fetch client for better testability
     * CORE-REF-002 FIX: Return type now uses UniversalisItemResult for consistency
     */
    async fetchWithTimeout(url, timeoutMs) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await this.fetchClient.fetch(url, { signal: controller.signal });
            if (!response.ok) {
                // OPT-014: typed error so the retry predicate can skip deterministic 4xx
                throw new HttpError(response.status, `HTTP ${response.status}: ${response.statusText}`);
            }
            const contentType = response.headers.get('content-type');
            if (!contentType?.includes('application/json')) {
                throw new Error('Invalid content type: expected application/json');
            }
            // Check content-length header if available
            const contentLength = response.headers.get('content-length');
            if (contentLength) {
                const size = parseInt(contentLength, 10);
                if (!isNaN(size) && size > API_MAX_RESPONSE_SIZE) {
                    throw new Error(`Response too large: ${size} bytes (max: ${API_MAX_RESPONSE_SIZE} bytes)`);
                }
            }
            // Read response text and validate size
            const text = await response.text();
            if (text.length > API_MAX_RESPONSE_SIZE) {
                throw new Error(`Response too large: ${text.length} bytes (max: ${API_MAX_RESPONSE_SIZE} bytes)`);
            }
            // Parse JSON with error handling
            try {
                return JSON.parse(text);
            }
            catch (parseError) {
                throw new Error(`Invalid JSON response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`, { cause: parseError });
            }
        }
        finally {
            clearTimeout(timeoutId);
        }
    }
    /**
     * Parse and validate API response
     * CORE-REF-002 FIX: Now uses shared extractPriceFromApiItem() helper
     */
    parseApiResponse(data, itemID) {
        try {
            // Validate response structure
            if (!data || typeof data !== 'object') {
                this.logger.warn(`Invalid API response structure for item ${itemID}`);
                return null;
            }
            // Parse aggregated endpoint response format
            if (!data.results || !Array.isArray(data.results) || data.results.length === 0) {
                this.logger.warn(`No price data available for item ${itemID}`);
                return null;
            }
            const result = data.results[0];
            if (!result || typeof result !== 'object') {
                this.logger.warn(`Invalid result structure for item ${itemID}`);
                return null;
            }
            // Validate itemId type and match
            if (typeof result.itemId !== 'number' || result.itemId !== itemID) {
                this.logger.warn(`Item ID mismatch: expected ${itemID}, got ${result.itemId}`);
                return null;
            }
            // CORE-REF-002 FIX: Use shared price extraction helper
            const { price, worldId } = extractPriceFromApiItem(result);
            if (!price) {
                this.logger.warn(`No price data available for item ${itemID}`);
                return null;
            }
            return {
                itemID,
                currentAverage: Math.round(price),
                currentMinPrice: Math.round(price), // Using same price for all fields
                currentMaxPrice: Math.round(price),
                lastUpdate: Date.now(),
                worldId,
            };
        }
        catch (error) {
            this.logger.error(`Failed to parse price data for item ${itemID}`, error);
            return null;
        }
    }
    /**
     * Parse batch API response containing multiple items
     * Returns a Map of itemID -> PriceData for all successfully parsed items,
     * or null when the body is not a batch response at all (BUG-090: the
     * caller reports that as a failed request rather than an empty board)
     * CORE-REF-002 FIX: Now uses shared extractPriceFromApiItem() helper
     */
    parseBatchApiResponse(data) {
        const results = new Map();
        try {
            // Validate response structure
            if (!data || typeof data !== 'object') {
                this.logger.warn('Invalid batch API response structure');
                return null;
            }
            if (!data.results || !Array.isArray(data.results)) {
                this.logger.warn('No results array in batch API response');
                return null;
            }
            // Parse each item in the results array
            for (const result of data.results) {
                if (!result || typeof result !== 'object' || typeof result.itemId !== 'number') {
                    continue;
                }
                const itemID = result.itemId;
                // CORE-REF-002 FIX: Use shared price extraction helper
                const { price, worldId } = extractPriceFromApiItem(result);
                if (price) {
                    results.set(itemID, {
                        itemID,
                        currentAverage: Math.round(price),
                        currentMinPrice: Math.round(price),
                        currentMaxPrice: Math.round(price),
                        lastUpdate: Date.now(),
                        worldId,
                    });
                }
            }
            // Log any failed items
            if (data.failedItems && data.failedItems.length > 0) {
                this.logger.warn(`Batch request had ${data.failedItems.length} failed items: ${data.failedItems.join(', ')}`);
            }
            return results;
        }
        catch (error) {
            this.logger.error('Failed to parse batch price data', error);
            return results;
        }
    }
    /**
     * Build API URL for item price query
     */
    buildApiUrl(itemID, _worldID, dataCenterID) {
        // Universalis API endpoint: /api/v2/aggregated/{dataCenter or worldName}/{itemIDs}
        // Note: worldID parameter reserved for future use (world-specific queries)
        // Sanitize dataCenterID to prevent URL path injection
        const pathSegment = dataCenterID ? this.dataCenterPathSegment(dataCenterID) : 'universal';
        return `${this.baseUrl}/aggregated/${pathSegment}/${itemID}`;
    }
    /**
     * Build API URL for batch item price query
     * Universalis supports comma-separated item IDs in a single request
     * Example: /api/v2/aggregated/Crystal/5729,5730,5731
     *
     * INPUT-001: Added validation for array contents and length
     */
    buildBatchApiUrl(itemIDs, dataCenterID) {
        // INPUT-001: Validate array is not empty
        if (!itemIDs || itemIDs.length === 0) {
            throw new AppError(ErrorCode.INVALID_INPUT, 'itemIDs array cannot be empty', 'warning');
        }
        // INPUT-001: Validate array size (Universalis recommends max 100 items per request)
        if (itemIDs.length > 100) {
            throw new AppError(ErrorCode.INVALID_INPUT, `Cannot fetch more than 100 items in one batch (got ${itemIDs.length})`, 'warning');
        }
        // INPUT-001: Validate each item ID is a positive integer
        const invalidIds = itemIDs.filter((id) => !Number.isInteger(id) || id < 1);
        if (invalidIds.length > 0) {
            throw new AppError(ErrorCode.INVALID_INPUT, `Invalid item IDs: all IDs must be positive integers (invalid: ${invalidIds.slice(0, 5).join(', ')}${invalidIds.length > 5 ? '...' : ''})`, 'warning');
        }
        // Sanitize dataCenterID to prevent URL path injection
        const pathSegment = dataCenterID ? this.dataCenterPathSegment(dataCenterID) : 'universal';
        // Join item IDs with commas for batch request
        const itemsSegment = itemIDs.join(',');
        return `${this.baseUrl}/aggregated/${pathSegment}/${itemsSegment}`;
    }
    /**
     * Sanitize a data center or world name for the URL path and the cache key.
     * Keeps Unicode letters and digits only, so '/', '.', '%', '?', '#', '\'
     * and whitespace are all stripped — no traversal, no extra path segment,
     * no pre-encoded escape.
     *
     * Sprint 27 review: this used to keep ASCII letters and digits only, which
     * reduced every Chinese and Korean data center and world (陆行鸟, 한국,
     * 红玉海, …) to '' — an `/aggregated//<ids>` URL that the proxy 404s, and
     * one shared `dc:` cache key for all of them.
     */
    sanitizeDataCenterId(dataCenterID) {
        return dataCenterID.replace(/[^\p{L}\p{N}]/gu, '');
    }
    /**
     * The sanitized name, percent-encoded for the URL path (a CJK name is not
     * a valid raw path segment). Sanitize first: encoding first would produce
     * '%' escapes the sanitizer then strips. UTF-8 never encodes a non-ASCII
     * character with an ASCII byte, so no encoded letter can become '%2F'.
     */
    dataCenterPathSegment(dataCenterID) {
        return encodeURIComponent(this.sanitizeDataCenterId(dataCenterID));
    }
    /**
     * Build cache key from parameters
     * SECURITY: Uses type prefixes to prevent cache key collisions
     * Format: itemID:type:value where type is 'dc', 'world', or 'global'
     * This prevents crafted dataCenterID values from colliding with other key patterns
     */
    buildCacheKey(itemID, worldID, dataCenterID) {
        const parts = [itemID];
        if (dataCenterID) {
            // Type prefix 'dc' ensures datacenter keys don't collide with world/global keys
            const sanitized = this.sanitizeDataCenterId(dataCenterID);
            parts.push('dc', sanitized);
        }
        else if (worldID) {
            // Type prefix 'world' ensures world keys don't collide with datacenter/global keys
            parts.push('world', worldID);
        }
        else {
            parts.push('global');
        }
        // Use colon delimiter (not in sanitized values) for unambiguous parsing
        return parts.join(':');
    }
    // ============================================================================
    // Batch Operations
    // ============================================================================
    /**
     * Fetch prices for dyes in a specific data center
     * PERFORMANCE: Uses batched API requests to minimize rate limiting
     *
     * Upstream failures are absorbed: an unreachable board answers with the
     * cached prices only (often an empty Map), exactly like a board with no
     * listings. Call {@link APIService.getPricesForDataCenterWithOutcome} to
     * tell the two apart.
     *
     * @public
     * @param itemIDs - Market item IDs to price
     * @param dataCenterID - Data center (or world) name, e.g. "Crystal"
     * @returns Map of itemID -> PriceData for every cached or fetched item
     */
    async getPricesForDataCenter(itemIDs, dataCenterID) {
        const { prices } = await this.getPricesForDataCenterWithOutcome(itemIDs, dataCenterID);
        return prices;
    }
    /**
     * Fetch prices for dyes in a specific data center, and report how the
     * upstream half of the lookup went.
     * PERFORMANCE: Uses batched API requests to minimize rate limiting
     *
     * Strategy:
     * 1. Check cache for each item
     * 2. Collect uncached items
     * 3. Fetch uncached items in a single batched request (chunked past 100)
     * 4. Store fetched items in cache
     *
     * BUG-090 (2026-10-04 deep-dive): `getPricesForDataCenter` returns the same
     * prices but cannot say whether an empty Map means "no listings" or "the
     * board is unreachable"; `outcome` does. A failed request is `'error'` when
     * the returned Map is empty and `'partial'` when it holds any price, cached
     * or fetched. Never throws — a failing upstream request, unparseable body
     * or injected rate limiter is reported through `outcome`.
     *
     * @param itemIDs - Market item IDs to price
     * @param dataCenterID - Data center (or world) name, e.g. "Crystal"
     * @returns The prices (identical to `getPricesForDataCenter`'s Map) and the
     *   {@link PriceBatchOutcome}
     */
    async getPricesForDataCenterWithOutcome(itemIDs, dataCenterID) {
        const results = new Map();
        const uncachedItemIDs = [];
        // Step 1: Check cache for each item (in parallel for async backends)
        const cacheChecks = await Promise.all(itemIDs.map(async (itemID) => {
            const cacheKey = this.buildCacheKey(itemID, undefined, dataCenterID);
            const cached = await this.getCachedPrice(cacheKey);
            return { itemID, cached };
        }));
        for (const { itemID, cached } of cacheChecks) {
            if (cached) {
                results.set(itemID, cached);
            }
            else {
                uncachedItemIDs.push(itemID);
            }
        }
        // Step 2: If all items are cached, return immediately
        if (uncachedItemIDs.length === 0) {
            this.logger.debug(`All ${itemIDs.length} items found in cache for ${dataCenterID}`);
            return { prices: results, outcome: 'ok' };
        }
        this.logger.debug(`Fetching ${uncachedItemIDs.length} uncached items (${results.size} cached) for ${dataCenterID}`);
        // Step 3+4: Fetch uncached items and write them to cache.
        // OPT-001 (2026-07-18 audit): identical in-flight batches are coalesced so
        // N concurrent cold-cache callers produce one upstream request, mirroring
        // the single-item pendingRequests dedupe. The entry is removed on settle.
        // BUG-011: cache writes are best-effort — one failed write must not throw
        // away the whole fetched batch.
        const batchKey = `${[...uncachedItemIDs].sort((a, b) => a - b).join(',')}:${dataCenterID}`;
        let batchPromise = this.pendingBatchRequests.get(batchKey);
        if (!batchPromise) {
            batchPromise = (async () => {
                const batchResult = await this.fetchBatchPriceData(uncachedItemIDs, dataCenterID);
                const cacheWrites = [];
                for (const [itemID, priceData] of batchResult.prices) {
                    const cacheKey = this.buildCacheKey(itemID, undefined, dataCenterID);
                    cacheWrites.push(this.trySetCachedPrice(cacheKey, priceData));
                }
                await Promise.all(cacheWrites);
                return batchResult;
            })().finally(() => {
                this.pendingBatchRequests.delete(batchKey);
            });
            this.pendingBatchRequests.set(batchKey, batchPromise);
        }
        const { prices: fetched, failed } = await batchPromise;
        for (const [itemID, priceData] of fetched) {
            results.set(itemID, priceData);
        }
        // BUG-090: a failed request is 'error' only when the caller gets no price
        // at all. Any price in the returned Map -- cached, or fetched by another
        // chunk of a >100-item call -- makes it 'partial' (Sprint 27 review: an
        // 'error' beside cached prices had consumers call every price missing
        // while they were showing some).
        const outcome = !failed ? 'ok' : results.size > 0 ? 'partial' : 'error';
        return { prices: results, outcome };
    }
    // ============================================================================
    // Health Checks
    // ============================================================================
    /**
     * Check if Universalis API is available
     * Uses injected fetch client
     */
    async isAPIAvailable() {
        // BUG-057: this was the only fetch in the file without a timeout, so an
        // upstream that accepts the connection and then stalls left the
        // availability probe hanging indefinitely — the one call whose entire job
        // is to answer quickly. Every other request goes through fetchWithTimeout,
        // which is not reusable here because it also parses a JSON body.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), UNIVERSALIS_API_TIMEOUT);
        try {
            const response = await this.fetchClient.fetch(`${this.baseUrl}/data-centers`, {
                signal: controller.signal,
            });
            return response.ok;
        }
        catch {
            return false;
        }
        finally {
            clearTimeout(timeoutId);
        }
    }
    /**
     * Get API status information
     */
    async getAPIStatus() {
        // core-data-15: this used to wrap the call in a try/catch returning
        // `{ available: false, latency: -1 }`. That branch was UNREACHABLE:
        // `isAPIAvailable()` has its own catch-all returning `false`, so it cannot
        // reject. The only test that reached it did so by mocking `isAPIAvailable`
        // to reject, which proves the dead branch works rather than that anything
        // can get to it -- and the sentinel latency of -1 would have masked a real
        // failure as a value rather than surfacing it. Removed: if
        // `isAPIAvailable` ever does start throwing, the caller should see it.
        const start = Date.now();
        const available = await this.isAPIAvailable();
        return { available, latency: Date.now() - start };
    }
    // ============================================================================
    // Utility Methods
    // ============================================================================
    /**
     * Format price for display (FFXIV Gil format: 69,420G)
     */
    static formatPrice(price) {
        const formattedNumber = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
        // Return with small "G" suffix
        return `${formattedNumber}G`;
    }
}
//# sourceMappingURL=APIService.js.map